import React, { ReactNode } from 'react'

import { Controller, useForm } from 'react-hook-form'

import { CustomButton } from '@components/CustomButton'
import { FormDatePicker } from '@components/Form/FormDatePicker'
import { FormInput } from '@components/Form/FormInput'
import { FormMultiselect } from '@components/Form/FormMultiselect'
import { FormSelect } from '@components/Form/FormSelect'
import { FormSwitch } from '@components/Form/FormSwitch'
import { FormHeader } from '@containers/Headers/FormHeader'
import { useFindOperationPosts } from '@hooks/catalogs/operationPosts'
import { useFindPermits } from '@hooks/catalogs/permitTypes'
import { useSiteFeatures } from '@hooks/siteFeatures'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'

import { AddResourceButton } from './AddResourceButton'
import { TTaskForm, useTaskForm } from './schema'
import { useStartDateField } from './startDate'
import { ButtonContainer, FormContainer } from './styles'

interface IProps {
  onClose: () => void
  onSave: (data: TTaskForm) => void
  title: string
  task?: IGenericTask
  onAddAssignment?: (duration: number, currentValues: TTaskForm) => void
  onDelete?: () => void
  children?: ReactNode
  isFormPrefilled?: boolean
}

export function TaskForm({
  onClose,
  onSave,
  title,
  task,
  onAddAssignment,
  onDelete,
  children,
  isFormPrefilled = false,
}: IProps) {
  const { getResolvedValidationSchema } = useTaskForm()

  const { getStartDateRange } = useStartDateField()
  const { maxDate, minDate } = getStartDateRange()

  const { isMidstreamProcess } = useSiteFeatures()
  const { data: rawPermits } = useFindPermits()
  const permits = rawPermits ?? []
  const { data: rawOperationPosts } = useFindOperationPosts()
  const operationPosts = rawOperationPosts ?? []

  const isISTeTask = task && !!task.code

  const {
    formState: { isDirty },
    control,
    handleSubmit,
    watch,
    getValues,
  } = useForm<TTaskForm>({
    resolver: getResolvedValidationSchema(),
    defaultValues: {
      name: task?.name,
      duration: task?.duration,
      permitNeeded: task?.permitNeeded,
      permitStatus: task?.permitStatus,
      permitTypeIds: task?.permitTypeIds,
      operationPostId: task?.operationPostId,
      needsEquipmentRental: task?.needsEquipmentRental ?? false,
      isBreakIn: task?.isBreakIn ?? false,
      isComplete: task?.isComplete ?? false,
      startTime:
        (task?.startTime && new Date(task?.startTime)) ||
        new Date(new Date().setHours(9, 0, 0, 0)),
      selectedDate:
        (task?.startTime && new Date(task?.startTime)) || new Date(),
    },
  })

  const watchPermit = watch('permitNeeded')
  const watchDuration = watch('duration')
  const shouldRender = !isMidstreamProcess && watchPermit === 1

  const permitOptions = [
    { label: 'No', value: 0 },
    { label: 'Yes', value: 1 },
    { label: 'Unspecified', value: 2 },
  ]

  const operationsList = operationPosts
    .filter((o) => o.isActive && o.projectIds.includes(task?.projectId ?? -1))
    .map((o) => {
      return {
        label: o.description,
        value: o.id,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <FormHeader
      onPressLeft={onClose}
      isRightButtonDisabled={!isDirty && !isFormPrefilled}
      leftLabel="Close"
      rightLabel="Save"
      onPressRight={handleSubmit(onSave)}
      title={title}
    >
      <FormContainer>
        {!isMidstreamProcess && (
          <Controller
            control={control}
            name="name"
            render={({
              field: { onChange },
              fieldState: { error },
              formState: { defaultValues },
            }) => (
              <FormInput
                isRequired
                placeholder="Enter Task Name"
                label="Name"
                defaultValue={defaultValues?.name}
                onChange={(e) => onChange(e.target.value)}
                errorMessage={error?.message}
                isDisabled={isISTeTask}
              />
            )}
          />
        )}

        {!isMidstreamProcess && (
          <Controller
            control={control}
            name="duration"
            render={({
              field: { onChange },
              fieldState: { error },
              formState: { defaultValues },
            }) => (
              <FormInput
                isRequired
                placeholder="Enter Task Duration"
                label="Duration (hours)"
                defaultValue={defaultValues?.duration?.toString()}
                onChange={(e) => onChange(e.target.value)}
                errorMessage={error?.message}
                isDisabled={isISTeTask}
              />
            )}
          />
        )}

        {!isMidstreamProcess && (
          <Controller
            control={control}
            name="permitNeeded"
            render={({
              field: { onChange },
              fieldState: { error },
              formState: { defaultValues },
            }) => (
              <FormSelect
                mb={4}
                isRequired
                placeholder="Is Permit Needed?"
                defaultValue={defaultValues?.permitNeeded?.toString()}
                label="Permit Needed"
                errorMessage={error?.message}
                items={permitOptions}
                onValueChange={(value) => onChange(parseInt(value))}
              />
            )}
          />
        )}

        {shouldRender && (
          <Controller
            control={control}
            name="permitTypeIds"
            render={({
              field: { onChange },
              fieldState: { error },
              formState: { defaultValues },
            }) => (
              <FormMultiselect
                isRequired
                errorMessage={error?.message}
                placeHolder="Select a Permit Type"
                onChange={onChange}
                items={permits}
                defaultValues={defaultValues?.permitTypeIds}
                label="Permit Type"
              />
            )}
          />
        )}

        {shouldRender && (
          <Controller
            control={control}
            name="permitStatus"
            render={({
              field: { onChange },
              fieldState: { error },
              formState: { defaultValues },
            }) => (
              <FormSelect
                mb={4}
                isRequired
                placeholder="Is Permit Ready?"
                defaultValue={defaultValues?.permitStatus?.toString()}
                errorMessage={error?.message}
                label="Permit Ready"
                items={permitOptions}
                onValueChange={(value) => onChange(parseInt(value))}
              />
            )}
          />
        )}

        {!isMidstreamProcess && (
          <Controller
            control={control}
            name="operationPostId"
            render={({
              field: { onChange },
              fieldState: { error },
              formState: { defaultValues },
            }) => (
              <FormSelect
                mb={4}
                defaultValue={defaultValues?.operationPostId?.toString()}
                label="Post"
                placeholder="Select an Operation Post"
                errorMessage={error?.message}
                items={operationsList}
                onValueChange={(value) => onChange(parseInt(value))}
              />
            )}
          />
        )}

        {!isMidstreamProcess && (
          <Controller
            control={control}
            name="needsEquipmentRental"
            render={({ field: { onChange, value } }) => (
              <FormSwitch
                mb={4}
                label="Equipment Rental?"
                onToggle={onChange}
                value={value}
              />
            )}
          />
        )}

        {!isISTeTask && (
          <Controller
            control={control}
            name="isBreakIn"
            render={({ field: { onChange, value } }) => (
              <FormSwitch
                mb={4}
                label="Is Break In?"
                onToggle={onChange}
                value={value}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="isComplete"
          render={({ field: { onChange, value } }) => (
            <FormSwitch
              mb={4}
              label="Is Complete?"
              onToggle={onChange}
              value={value}
            />
          )}
        />

        <Controller
          control={control}
          name="selectedDate"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormDatePicker
              isRequired
              label="Start Date"
              onChange={onChange}
              minimumDate={minDate}
              maximumDate={maxDate}
              value={value}
              mode="date"
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="startTime"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormDatePicker
              isRequired
              label="Start Time"
              onChange={onChange}
              value={value || new Date(new Date().setHours(9, 0, 0, 0))}
              mode="time"
              errorMessage={error?.message}
            />
          )}
        />
      </FormContainer>

      {children && <>{children}</>}

      {onAddAssignment && (
        <AddResourceButton
          duration={watchDuration}
          onAddAssignment={() => onAddAssignment(watchDuration, getValues())}
        />
      )}

      {onDelete && !isISTeTask && (
        <ButtonContainer>
          <CustomButton onPress={onDelete}>Delete Task</CustomButton>
        </ButtonContainer>
      )}
    </FormHeader>
  )
}
